import { Dispatch, SetStateAction, useState } from "react";
import { format, startOfDay, endOfDay, addWeeks, subWeeks, addDays, isSameDay } from "date-fns";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { colors } from "../../../theme/palette";
import { CalendarLeftIcon, CalendarRightIcon } from "../../../assets/icons/MUI/EventsIcons";
import { useTranslation } from "react-i18next";

import { enUS, arSA } from "date-fns/locale";

const WeeklyCalendar = ({
  setMinDate,
  setMaxDate,
}: {
  setMinDate: Dispatch<SetStateAction<string>>;
  setMaxDate: Dispatch<SetStateAction<string>>;
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());

  const handlePrevWeek = () => {
    setCurrentWeek(subWeeks(currentWeek, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, 1));
  };

  const handleDayClick = (day: Date) => {
    setSelectedDay(day);
    setMinDate(startOfDay(day).toISOString());
    setMaxDate(endOfDay(day).toISOString());
  };

  const locale = currentLang === "ar" ? arSA : enUS;

  const renderWeekDays = () => {
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = addDays(currentWeek, i);
      const isSelected = isSameDay(day, selectedDay);

      weekDays.push(
        <Box
          key={i}
          sx={{ width: "42px", display: "flex", flexDirection: "column", alignItems: "center", rowGap: "5px" }}
        >
          <Button
            onClick={() => handleDayClick(day)}
            sx={{
              minWidth: "unset",
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              background: isSelected ? colors.Green100 : colors.White,
              color: isSelected ? colors.White : colors.Black,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="body13Bold">{format(day, "dd", { locale })}</Typography>
            <Typography variant="body10Regular">{format(day, "MMM", { locale }).toUpperCase()}</Typography>
          </Button>
          <Typography variant="body12SemiBold" sx={{ color: isSelected ? colors.Green100 : colors.Dark600 }}>
            {format(day, "EEE", { locale }).toUpperCase()}
          </Typography>
        </Box>
      );
    }
    return weekDays;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        background: "#F7F8F8",
        borderRadius: "11px",
      }}
    >
      <IconButton
        onClick={handlePrevWeek}
        sx={{ background: colors.Green25, height: "87.5px", borderRadius: "11px 0 0 11px" }}
      >
        {currentLang === "en" ? <CalendarLeftIcon /> : <CalendarRightIcon />}
      </IconButton>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          columnGap: "16px",
          flex: 1,
          padding: "10px 0",
        }}
      >
        {renderWeekDays()}
      </Box>
      <IconButton
        onClick={handleNextWeek}
        sx={{ background: colors.Green25, height: "87.5px", borderRadius: "0 11px 11px 0" }}
      >
        {currentLang === "en" ? <CalendarRightIcon /> : <CalendarLeftIcon />}
      </IconButton>
    </Box>
  );
};

export default WeeklyCalendar;
